import React, { useEffect, useState } from 'react';
import { WhatsAppButton, useWhatsAppPublic } from '../../components/WhatsApp.jsx';
import { Link, useLocation, useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { useAuth } from '../../auth.jsx';
import { useLive } from '../../live.jsx';
import { categoryName, formatDate } from '../../format.js';
import { incotermLabel } from '../../productOptions.js';
import NotFound from '../site/NotFound.jsx';
import { Alert } from '../../components/ui.jsx';
import OfferForm from './OfferForm.jsx';
import { flag, formatQty, money, unitLabel, destination, timeframeText, shortDate } from './requestUtils.js';
import './requests.css';

// /marketplace/requests/:id — public, indexable page of a buying request.
// Never any contact details; an anonymous request shows "Confidential
// buyer". "Make an offer" for signed-in members (not the requester).
export default function RequestDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const location = useLocation();
  const [r, setR] = useState(null);
  const [missing, setMissing] = useState(false);
  const [offering, setOffering] = useState(false);
  const [sent, setSent] = useState('');
  const [original, setOriginal] = useState(false);
  const [tick, setTick] = useState(0);
  const wa = useWhatsAppPublic();
  usePageTitle(null);
  useLive(['requests'], () => setTick((x) => x + 1));

  useEffect(() => {
    let alive = true;
    api(`/buying-requests/${id}?lang=${lang}`).then((d) => alive && setR(d)).catch(() => alive && setMissing(true));
    return () => { alive = false; };
  }, [id, lang, tick, user?.id]);
  useEffect(() => {
    if (!r) return;
    document.title = `${t('buying.wanted')} : ${r.title} | ${t('brand.name')}`;
    // Closed / expired requests stay indexable as "closed"; pending / rejected ones never are.
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'robots'; document.head.appendChild(meta); }
    meta.content = r.publishedAt && !['pending', 'rejected'].includes(r.status) ? 'index, follow' : 'noindex';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.content = `${t('buying.wanted')}: ${r.title} — ${formatQty(r.quantity, r.unit, lang, t)}${r.destCountry ? ` → ${destination(r, lang)}` : ''}`.slice(0, 300);
  }, [r, t, lang]);
  useEffect(() => () => { const m = document.querySelector('meta[name="robots"]'); if (m) m.content = 'index, follow'; }, []);

  if (missing) return <NotFound />;
  if (!r) return <div className="page-loading">{t('common.loading')}</div>;

  const showOriginal = original && r.original;
  const title = showOriginal ? r.original.title : r.title;
  const html = showOriginal ? r.original.descriptionHtml : r.descriptionHtml;
  const dest = destination(r, lang);
  const when = timeframeText(r, lang);
  const specGroups = new Map();
  for (const s of r.specs) {
    const g = specGroups.get(s.groupId) || { label: lang === 'en' ? s.labelEn || s.labelFr : s.labelFr, values: [] };
    g.values.push(lang === 'en' ? s.valueEn : s.value);
    specGroups.set(s.groupId, g);
  }
  const canOffer = r.open && !r.mine;

  return (
    <div className="container page">
      <p><Link to="/marketplace?tab=requests" className="back-link">{t('buying.detail.back')}</Link></p>
      {r.categoryPath?.length > 0 && (
        <nav className="listing-crumbs" aria-label={t('wizard.breadcrumb')}>
          {r.categoryPath.map((c, i) => (
            <React.Fragment key={c.slug}>{i > 0 && <span aria-hidden="true">›</span>}<Link to={`/marketplace?tab=requests&category=${encodeURIComponent(c.slug)}`}>{lang === 'en' ? c.nameEn : c.nameFr}</Link></React.Fragment>
          ))}
        </nav>
      )}
      <div className="req-detail">
        <article>
          <div className="req-card-top">
            <span className="wanted-badge">{t('buying.wanted')}</span>
            <span className={`chip-status req-status-${r.status}`}>{t(`buying.status.${r.status}`)}</span>
          </div>
          <h1>{title}</h1>
          {r.translated && (
            <p className="req-translated">{t('buying.detail.translated')}{r.original && <button type="button" className="btn btn-link btn-sm" onClick={() => setOriginal(!original)}>{original ? t('buying.detail.showTranslation') : t('buying.detail.showOriginal')}</button>}</p>
          )}
          {r.status === 'pending' && <p className="callout callout-info">{t('buying.detail.pendingNote')}</p>}
          {r.status === 'rejected' && r.rejectionReason && <p className="callout callout-warning">{t('buying.detail.rejectedNote', { reason: r.rejectionReason })}</p>}
          {!r.open && !['pending', 'rejected'].includes(r.status) && <p className="req-banner closed" role="status">{t('buying.detail.closedBanner')}</p>}
          <dl className="req-keyfacts">
            <div><dt>{t('buying.quantity')}</dt><dd>{formatQty(r.quantity, r.unit, lang, t)}</dd></div>
            <div><dt>{t('buying.targetPrice')}</dt><dd>{r.priceOnRequest ? t('buying.priceOnRequest') : `${money(r.targetPrice, r.currency, lang)}${r.unit ? ` / ${unitLabel(r.unit, t)}` : ''}`}</dd></div>
            {dest && <div><dt>{t('buying.destination')}</dt><dd>{r.destCountry && <span aria-hidden="true">{flag(r.destCountry)} </span>}{dest}</dd></div>}
            {r.incoterm && <div><dt>Incoterm</dt><dd>{incotermLabel(r.incoterm, t)}</dd></div>}
            {when && <div><dt>{r.deliveryBy ? t('buying.detail.deliveryBy') : t('buying.timeframe')}</dt><dd>{when}</dd></div>}
            {r.paymentTerms.length > 0 && <div><dt>{t('buying.detail.paymentTerms')}</dt><dd>{r.paymentTerms.map((c) => t(`payment.${c}Long`)).join(', ')}</dd></div>}
          </dl>
          {r.certifications.length > 0 && (
            <ul className="chip-list listing-certs" aria-label={t('buying.detail.certifications')}>{r.certifications.map((c) => <li key={c} className="chip">✓ {c}</li>)}</ul>
          )}
          {specGroups.size > 0 && (
            <section>
              <h2>{t('buying.detail.specs')}</h2>
              <table className="specs-table"><tbody>{[...specGroups.values()].map((g) => <tr key={g.label}><th scope="row">{g.label}</th><td>{g.values.join(', ')}</td></tr>)}</tbody></table>
            </section>
          )}
          {html && (
            <section>
              <h2>{t('buying.detail.details')}</h2>
              {/* Sanitized on the server: only p, br, strong, em, ul, ol, li. */}
              <div className="rich-text" dangerouslySetInnerHTML={{ __html: html }} />
            </section>
          )}
          {r.photos.length > 0 && <div className="req-photos">{r.photos.map((p) => <a key={p.id} href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt="" loading="lazy" /></a>)}</div>}
          {r.attachments.length > 0 && (
            <section><h2>{t('buying.detail.attachments')}</h2><p className="listing-files">{r.attachments.map((a) => <a key={a.id} className="btn btn-ghost btn-sm" href={a.url} target="_blank" rel="noreferrer">📄 {a.name}</a>)}</p></section>
          )}
          <p className="muted small">{r.publishedAt && t('buying.postedOn', { date: formatDate(r.publishedAt, lang) })} · {t(r.open ? 'buying.expires' : 'buying.expired', { date: shortDate(r.expiresOn, lang) })}</p>
        </article>

        <aside className="req-aside">
          <section className="card">
            <p className="card-title">{t('buying.buyer')}</p>
            {r.buyer && !r.anonymous ? (
              <p className="strong" style={{ margin: 0 }}>{r.buyer.country && <span aria-hidden="true">{flag(r.buyer.country)} </span>}{r.buyer.companyName}</p>
            ) : <p style={{ margin: 0 }}>🔒 {t('buying.anonymousBuyer')}</p>}
            <p className="muted small" style={{ margin: '6px 0 0' }}>{r.offers ? t('buying.offersN', { n: r.offers }) : t('buying.noOffers')} · {categoryName(r.category, lang)}</p>
          </section>
          <section className="card" id="make-offer">
            {r.mine ? (
              <><p>{t('buying.detail.own')}</p><Link className="btn btn-primary btn-block" to={`/account/buying-requests/${r.id}`}>{t('buying.detail.manage')}</Link></>
            ) : !r.open ? (
              <p className="muted" style={{ margin: 0 }}>{t('buying.detail.closedBanner')}</p>
            ) : !user ? (
              <Link className="btn btn-primary btn-block" to={`/login?next=${encodeURIComponent(location.pathname)}`}>{t('buying.detail.loginToOffer')}</Link>
            ) : offering ? (
              <><h2 style={{ marginTop: 0 }}>{r.myOffer ? t('buying.offer.reviseTitle') : t('buying.offer.title')}</h2>
                <OfferForm request={r} onCancel={() => setOffering(false)} onDone={() => { setOffering(false); setSent(r.anonymous ? t('buying.offer.sentAnon') : t('buying.offer.sent')); setTick((x) => x + 1); }} /></>
            ) : (
              <>
                {sent && <Alert kind="success">{sent}</Alert>}
                {r.myOffer && <p><span className={`chip-status offer-status-${r.myOffer.status}`}>{t('buying.detail.yourOffer', { status: t(`buying.offerStatus.${r.myOffer.status}`) })}</span></p>}
                {canOffer && <button type="button" className="btn btn-primary btn-block" onClick={() => setOffering(true)}>{r.myOffer && r.myOffer.status !== 'withdrawn' ? t('buying.detail.reviseOffer') : t('buying.detail.makeOffer')}</button>}
              </>
            )}
          </section>
          {wa?.number && !r.mine && (
            <WhatsAppButton number={wa.number} className="btn-block" label={t('wa.askRequest')}
              text={t('wa.requestMessage', { name: r.title, url: `${window.location.origin}/marketplace/requests/${r.id}` })} />
          )}
        </aside>
      </div>
    </div>
  );
}
